import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EMD_TABS, EmdFilterService } from './emd-filter.service';

/**
 * Dimensions a visual can cross-filter on. Adding one here is all that is
 * needed to start staging by it — the service is dimension-agnostic.
 */
export type EmdDimension = 'wholesaler' | 'period';

export interface EmdSelectionChip {
  dimension: EmdDimension;
  value: string;
}

type SelectionState = Partial<Record<EmdDimension, string[]>>;

/**
 * In-progress ("pending") cross-filter selection for the EMD dashboard, in the
 * Qlik Sense sense: clicking values on a visual STAGES a selection that
 * previews live (cross-highlight) and raises the floating confirm/cancel
 * toolbar, but is NOT yet an applied filter.
 *
 * On confirm the shell writes the staged values into the EMD Filter Bar's own
 * badge dropdowns (EmdFilterService) — the single source of truth for applied
 * filters — and then clears this pending state. So this service never holds a
 * "committed" selection of its own and there is no separate selection chip: the
 * applied filter lives on the main badge filter.
 *
 * Provided on EmdDashboardComponent, so a staged selection is scoped to one
 * dashboard instance and survives tab switches until confirmed or cancelled.
 *
 * Click semantics follow Qlik Sense: every plain click TOGGLES the value in
 * the pending selection, so clicking several values accumulates them — no
 * modifier key needed. Clicking a staged value again removes it; removing the
 * last one dismisses the toolbar.
 */
@Injectable()
export class EmdSelectionService {
  private readonly pending$ = new BehaviorSubject<SelectionState>({});

  /** True while a selection is being made (the confirm/cancel toolbar is up). */
  pendingActive = false;

  /**
   * Id of the widget the user is selecting in — each staging click re-points
   * it, and the per-widget confirm/cancel toolbar shows only on that widget
   * (Qlik shows the toolbar on the active object, not globally).
   */
  origin: string | null = null;

  constructor(private readonly filters: EmdFilterService) {}

  /** Emits on every pending change (drives the live highlight preview). */
  readonly changes = this.pending$.asObservable();

  // ── Reads ─────────────────────────────────────────────────────────────────

  /** Staged values for a dimension. */
  pendingValues(dimension: EmdDimension): string[] {
    return this.pending$.value[dimension] ?? [];
  }

  /** What visuals highlight on — the staged selection (empty when idle). */
  effective(dimension: EmdDimension): string[] {
    return this.pendingActive ? this.pendingValues(dimension) : [];
  }

  isSelected(dimension: EmdDimension, value: string): boolean {
    return this.effective(dimension).indexOf(value) > -1;
  }

  /** True when `dimension` (or anything, if omitted) has a staged selection. */
  hasAny(dimension?: EmdDimension): boolean {
    if (!this.pendingActive) { return false; }
    const s = this.pending$.value;
    if (dimension) { return (s[dimension] ?? []).length > 0; }
    return Object.keys(s).some((k) => (s[k as EmdDimension] ?? []).length > 0);
  }

  /**
   * Whether `value` should be dimmed: something is staged on this dimension and
   * it is not this value. Visuals use this rather than computing it themselves
   * so highlight/dim stays consistent across the dashboard.
   */
  isDimmed(dimension: EmdDimension, value: string): boolean {
    return this.hasAny(dimension) && !this.isSelected(dimension, value);
  }

  get hasPending(): boolean {
    return this.pendingActive;
  }

  /** Flat list of staged selections, for the confirm toolbar's count/label. */
  pendingChips(): EmdSelectionChip[] {
    const s = this.pending$.value;
    return (Object.keys(s) as EmdDimension[]).flatMap((dimension) =>
      (s[dimension] ?? []).map((value) => ({ dimension, value }))
    );
  }

  // ── Staging ─────────────────────────────────────────────────────────────────

  /**
   * Toggle `value` in the pending selection. `origin` names the widget the
   * click came from, so its header can host the confirm/cancel toolbar.
   */
  stage(dimension: EmdDimension, value: string, origin = ''): void {
    this.pendingActive = true;
    if (origin) { this.origin = origin; }

    const current = this.pendingValues(dimension);
    const selected = current.indexOf(value) > -1;
    const next = selected ? current.filter((v) => v !== value) : [...current, value];

    this.setPending(dimension, next);

    // Deselecting the last staged value dismisses the toolbar.
    if (this.pendingChips().length === 0) {
      this.cancel();
    }
  }

  /**
   * Apply the staged selection by writing it into the MAIN badge filter — the
   * EMD Filter Bar's own dropdowns — rather than a separate chip. Wholesalers
   * land on every tab's Wholesaler badge so the cut holds across tab switches;
   * then the pending state is cleared. Lives here (not the shell) so each
   * widget's toolbar can confirm without routing the intent upward.
   */
  confirm(): void {
    const wholesalers = this.pendingValues('wholesaler');
    if (wholesalers.length) {
      for (const tab of EMD_TABS) {
        const chip = this.filters.chips(tab).find((c) => c.key === 'wholesaler');
        if (chip) {
          chip.selected = [...wholesalers];
          this.filters.apply(tab);
        }
      }
    }

    // A trend-bar selection is staged as week-ending dates; it becomes a
    // period cut on every tab (there is no badge for it — Reset clears it).
    const periods = this.pendingValues('period');
    if (periods.length) {
      for (const tab of EMD_TABS) {
        this.filters.setPeriods(tab, periods);
      }
    }

    this.cancel();
  }

  /** Discard the staged selection. */
  cancel(): void {
    this.pendingActive = false;
    this.origin = null;
    this.pending$.next({});
  }

  /** Alias kept for callers that "reset" the dashboard. */
  clear(): void {
    this.cancel();
  }

  private setPending(dimension: EmdDimension, values: string[]): void {
    const next: SelectionState = { ...this.pending$.value };
    if (values.length) { next[dimension] = values; } else { delete next[dimension]; }
    this.pending$.next(next);
  }
}
